import { useEffect, useRef, useState } from "react";

import PatientService from "../service/PatientService";
import { doctorNamesByPdf } from "./documentConstants";

/**
 * Who prescribed each of a patient's prescription PDFs, as a map from the PDF's document id to
 * the doctor's name (see doctorNamesByPdf) — the label beside a prescription in the documents
 * lists.
 *
 * ONE call per patient — prescription-service's doctors list, PDFs only — made the first time
 * `enabled` is true, so a list fetches it when it opens on prescriptions rather than once per
 * document.
 * Until it lands, or if it fails, the map is empty and every row shows its id alone, as before;
 * a failure is tried again the next time the list opens.
 */
const usePrescriptionDoctors = (patientId, enabled) => {
  const service = useRef(new PatientService()).current;
  const [state, setState] = useState({ patientId: null, names: {} });
  const requested = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const pid = patientId == null ? "" : String(patientId);
    if (!enabled || !pid || requested.current === pid) return;
    requested.current = pid;
    service.getPrescriptionDoctors(
      pid,
      (res) => {
        // A late answer for a patient this list has since moved off is dropped.
        if (mounted.current && requested.current === pid) {
          setState({ patientId: pid, names: doctorNamesByPdf(res) });
        }
      },
      () => {
        if (requested.current === pid) requested.current = null;
      }
    );
  }, [patientId, enabled, service]);

  return state.patientId === String(patientId) ? state.names : {};
};

export default usePrescriptionDoctors;
